import { isAxiosError } from 'axios'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { core } from '@musinsa/pbo-core'
import { pboLogisticsTheme } from '@musinsa/pbo-core/theme'
import { PboKoLocale } from '@/components/pbo/PboKoLocale'
import { PboConfirm } from '@/components/pbo/pboDialog'
import { failureError, invalidError, notice, rowInvalidError } from '@/components/pbo/notice'
import { withNewMarker, withoutNewMarker } from '@/components/pbo/newRowMarker'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import Loader from '@/components/content/Loader'
import { canSelectShipper, defaultShipper, scopedShipperCode, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { scopeSelectOptions } from '@/components/system/shipperScopeOptions'
import { useCoMingleShipperScope } from '@/components/system/useCoMingleShipperScope'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useLeaveGuard } from '@/components/layout/leaveGuard'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import {
  fromSearchQuery,
  GeneralOutboundSearchQuery,
  toSearchQuery,
} from '@/pages/outbound/generalOutbound/searchQuery'
import {
  ConfirmGeneralOutboundItem,
  EDITED_FLAG,
  ERROR_SKU_UNMAPPED,
  GeneralOutboundDownloadParams,
  GeneralOutboundFilter as FilterValues,
} from '@/services/outbound/generalOutbound/types'
import { hashKey, useQueryClient } from '@tanstack/react-query'
import { FETCH_ALL_MESSAGES, keepEditedSince, noticeIfUploadExceedsPage } from '@/hooks/useFetchAllRows'
import {
  GeneralOutboundListParams,
  generalOutboundQueryKeys,
  useConfirmGeneralOutbounds,
  useGetGeneralOutboundList,
  useGetOutboundProgress,
  useGetShipperOptions,
  useSaveGeneralOutbounds,
} from '@/services/outbound/generalOutbound/useGeneralOutboundQueries'
import {
  PROGRESS_COMPLETED,
  PROGRESS_FAILED,
  SAVE_ITEM_CODE,
  SAVE_MENU_CODE,
  SEARCH_MENU_CODE,
  GENERAL_OUTBOUND_FETCH_SIZE,
  GENERAL_OUTBOUND_DOWNLOAD_URL,
} from '@/services/outbound/generalOutbound/consts'
import {
  DELETE_MESSAGES,
  DOWNLOAD_REASON_OPTIONS,
  EXPORT_LOG_MENU_CD,
  MAX_SAVE_SIZE,
  MESSAGES,
  ORDER_TYPE_OPTIONS,
  REASON_DETAIL_MAX_LENGTH,
  REASON_DETAIL_MIN_LENGTH,
  SAVABLE_ORDER_TYPE_OPTIONS,
  SAVABLE_TRANSPORT_TYPE_OPTIONS,
  TRANSPORT_TYPE_OPTIONS,
  BE_REQUIRED_KEYS,
  COLUMNS,
  GRID_CDS,
  TABLE_ID,
  toRequiredColumns,
} from '@/pages/outbound/generalOutbound/components/consts'
import { withRequired } from '@/utils/requiredCells'
import {
  GeneralOutboundRow,
  toNewRow,
  toRows,
  toSaveItem,
  validateBeforeConfirm,
  validateBeforeSave,
} from '@/pages/outbound/generalOutbound/components/rows'
import { parseGeneralOutboundExcel } from '@/pages/outbound/generalOutbound/components/excelIo'
import { callToAddImportLog } from '@/services/excel/apis'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { DOWNLOAD_PASSWORD_INVALID_MESSAGE, isValidDownloadPassword } from '@/utils/downloadPassword'
import { SkuFailModal } from '@/pages/outbound/generalOutbound/components'
import { OutboundProgressModal } from '@/pages/outbound/components/OutboundProgressModal'
import GeneralOutboundPageView, { type ActionCtx, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'

/** 뷰가 onState 로 알리는 화면 상태(페이저·목록 검색어) */
type ViewState = Parameters<NonNullable<Wiring['onState']>>[0]

/** 아직 조회하지 않은 상태 — 화주코드가 비어 있으면 훅이 조회를 걸지 않는다. */
const EMPTY_FILTER: FilterValues = { SPR_NM: '' }

/** BE 응답에서 사람이 읽을 메시지를 꺼낸다. 2.0 일반출고는 1.0 형태(message)를 그대로 쓴다. */
const apiMessage = (error: unknown, fallback: string): string => {
  const resp = (error as { response?: { status?: number; data?: { message?: string; meta?: { message?: string } } } })?.response
  if (resp?.data?.message) return resp.data.message
  if (resp?.data?.meta?.message) return resp.data.meta.message
  if (resp?.status) return `${fallback} (HTTP ${resp.status})`
  /**
   * BE 가 HTTP 200 + `rtnChk:false` 로 실패를 내는 구간은 훅이 문구를 `Error.message` 에 담아 던진다.
   * `response` 가 없으니 위 분기에 안 걸린다 — 여기서 꺼내지 않으면 BE 사유가 통째로 버려진다.
   *
   * 다만 axios 오류는 걸러낸다. `AxiosError` 도 `Error` 라, 응답이 못 온 경우(네트워크 끊김·타임아웃)
   * 여기까지 내려와 "Network Error", "timeout of 0ms exceeded" 같은 영어가 사용자 알럿에 그대로 뜬다.
   */
  if (!isAxiosError(error) && error instanceof Error && error.message) return error.message
  return fallback
}

/** SKU 미해소 차단(422)이면 막힌 바코드 목록을 돌려준다. 아니면 null. */
const asSkuFailure = (
  error: unknown,
  title: string,
): { title: string; message: string; barcodes: string[] } | null => {
  const data = (error as { response?: { data?: { errorCode?: string; message?: string; failedBarcodes?: string[] } } })?.response?.data
  if (data?.errorCode !== ERROR_SKU_UNMAPPED) return null
  return { title, message: data.message ?? 'SKU 정보를 찾을 수 없습니다.', barcodes: data.failedBarcodes ?? [] }
}

/**
 * "이미 사용자에게 알린 실패" 표시 — 뷰 계약(컨덕터 #582)상 실패는 throw 해야 뷰가 선택·모달을 유지한다.
 * 슬롯 안에서 알럿을 띄운 뒤 throw 하는데, 바깥 catch 가 같은 오류를 다시 알리지 않게 표시를 남긴다.
 */
const SHOWN = Symbol('alertShown')
const markShown = (e: unknown): unknown => {
  if (e && typeof e === 'object') (e as Record<symbol, boolean>)[SHOWN] = true
  return e
}
const isAlreadyShown = (e: unknown): boolean => Boolean(e && typeof e === 'object' && (e as Record<symbol, boolean>)[SHOWN])

/** 저장 검증 문구가 특정 행을 짚는가 — 공통 문구(MESSAGES)가 아니면 `findMissingField` 의 "N번째 행의 …" 다. */
const COMMON_MESSAGES = new Set<string>(Object.values(MESSAGES))
const isRowMessage = (message: string): boolean => !COMMON_MESSAGES.has(message)

/** 다건 코드입력 값 — 뷰는 배열 또는 undefined 로 준다. 조작된 값이 와도 문자열만 받는다. */
const asList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String).filter(Boolean) : [])
const text = (v: unknown): string => (typeof v === 'string' ? v : '')

/** 뷰 조회영역 값(키 = filterKey) → 조회 조건. '전체'(빈 값)는 `undefined` 로 떨어뜨려 요청 본문을 짧게 유지한다(옛 필터 컴포넌트 toFilter). */
const toFilter = (values: Record<string, unknown>): FilterValues => ({
  SPR_NM: text(values.SPR_NM),
  ORD_NO: asList(values.ORD_NO),
  ORD_OPT_NO: asList(values.ORD_OPT_NO),
  ORD_TYPE: text(values.ORD_TYPE) || undefined,
  TRS_TYPE: text(values.TRS_TYPE) || undefined,
})

/** 복원한 조회 조건 → 조회영역 값(키 = filterKey). 없는 값은 얹지 않는다(기본값이 살아 있게). */
const restoredFilterValues = (filter: FilterValues): Record<string, unknown> => ({
  ...(filter.SPR_NM ? { SPR_NM: filter.SPR_NM } : {}),
  ...(filter.ORD_NO?.length ? { ORD_NO: filter.ORD_NO } : {}),
  ...(filter.ORD_OPT_NO?.length ? { ORD_OPT_NO: filter.ORD_OPT_NO } : {}),
  ...(filter.ORD_TYPE ? { ORD_TYPE: filter.ORD_TYPE } : {}),
  ...(filter.TRS_TYPE ? { TRS_TYPE: filter.TRS_TYPE } : {}),
})

/**
 * GI31 일반출고 — 1.0 GI31 을 신규 디자인(pbo-core)으로 재구현한 화면. (LOGISPRD-11238)
 *
 * 겉(제목·조회영역·표·액션바·엑셀 사유/업로드 모달)은 컨덕터 뷰(`./handoff/page.generated`, 재핸드오프 때 통째로 갱신)가 그리고,
 * 이 파일은 옛 훅·API·세션/URL 유지·잠금·검증을 그대로 두고 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드, 컨덕터 문서 51).
 * 옛 조립부(`components/GeneralOutboundFilter·Table·ExcelDownloadReasonModal·ExcelUploadModal·PboScope`)가 나눠 갖던 상태를 여기로 모았다 —
 * 표 선택·목록 검색·페이저·모달 열림·필터 폼은 뷰가 가지므로 그 짝은 지웠다.
 *
 * 조회·저장·확정 모두 moms-be(2.0) 를 호출한다. 1.0 도 GI31 만은 이미 같은 경로를 쓰고 있었다.
 *
 * 데모 대비 의도적 이탈(근거는 각 지점 주석):
 * - 주문유형·배송유형 옵션은 BE 열거형 기준이다. 데모 목록의 폐기·테스트·증정·재고이동은 2.0 에 없다(consts 주석).
 * - 엑셀 업로드는 서버 API 없이 화면에서 처리한다. 1.0 도 같았다(excelIo 주석).
 * - 엑셀 다운로드는 BE 가 마지막 조회 조건으로 다시 뽑아 비밀번호 zip 으로 내려준다(LOGISPRD-15797·15805). 1.0 GI31 도 비밀번호 zip 이었다.
 *   목록 검색어·저장 안 한 입력은 파일에 담기지 않는다. 감사로그(사유)도 BE 가 남긴다.
 * - 생성본을 직접 고쳤다(LOGISPRD-15797) — 엑셀 모달의 '파일 비밀번호' 칸(RGR05 생성본과 같은 요소)·열 때 비우는 필드에 password. 재핸드오프로 덮이면 seam.test 가 잡는다.
 * - SKU 미해소(422) 안내 모달은 데모에 없는 dev 오류 UI 다(LOGISPRD-10932). 막힌 바코드 전량을 보여 줘야 해 알럿으로 줄이지 않았다.
 */
function GeneralOutboundScreen({ coMingle }: { coMingle: boolean }) {
  // 조회 조건을 메뉴 세션(sessionStorage)·URL 에 남겨 다른 메뉴를 다녀와도 유지한다(결정 12, MOMS 공통 UX).
  const queryClient = useQueryClient()
  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const { parsedQs, replaceQuery } = useParsedQuery<GeneralOutboundSearchQuery>()
  // 세션 사용자는 마운트 후 바뀌지 않는다 — 렌더마다 sessionStorage 를 다시 읽지 않는다(마운트 스냅샷은 useRef, 문서 51 ⑩).
  const userRef = useRef<ReturnType<typeof AuthStorage.user.get>>()
  userRef.current ??= AuthStorage.user.get()
  const user = userRef.current
  const userId = user.email ?? ''
  // 화주 잠금(LOGISPRD-14653) — 통합 운영 화주 외 계정은 화주명 옵션이 자기 화주 하나고, 복원·조회 화주도 자기 화주로 강제한다(1.0 FilterBar 규칙).
  //  재고통합 ON 이면 잠긴 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고른다(LOGISPRD-15609, `withCoMingle`).
  const scope = useMemo(() => withCoMingle(shipperScopeOf(user), coMingle), [user, coMingle])
  /** 화주 기본값 — 세션 화주 코드. 재고통합 대상 계정은 MUSINSA 다(LOGISPRD-15718) */
  const defaultShipperCode = defaultShipper(scope).shipperCode
  /** 조회영역 값의 화주를 범위로 덮는다 — [조회] 와 뷰가 매 렌더 넘기는 현재 값(확정 요청·업로드 게이트가 본다) 둘 다 지난다. 고를 수 있는 화주는 그대로 둔다. */
  const lockFilter = (filter: FilterValues): FilterValues => (scope.locked ? { ...filter, SPR_NM: scopedShipperCode(scope, filter.SPR_NM) } : filter)
  /**
   * 마운트 1회만 복원한다 — 이후 URL 이 바뀌어도 입력을 덮어쓰지 않는다.
   * 화주 기본값은 여기서 기본 화주를 쓴다. 옵션 목록이 늦게 와도 코드 자체는 먼저 정해진다.
   */
  const restoredRef = useRef<ReturnType<typeof fromSearchQuery>>()
  restoredRef.current ??= fromSearchQuery(parsedQs, { SPR_NM: defaultShipperCode })
  // 잠금 계정은 URL 에 고를 수 없는 화주가 남아 있으면 기본 화주로 복원한다.
  if (scope.locked) restoredRef.current.filter.SPR_NM = scopedShipperCode(scope, restoredRef.current.filter.SPR_NM)
  const restored = restoredRef.current

  const [queryParams, setQueryParams] = useState<GeneralOutboundListParams>({
    MENU_CD: SEARCH_MENU_CODE,
    USR_ID: userId,
    // 복원된 조건이 있을 때만 채운다. 기본 화주를 미리 넣으면 업로드 게이트 같은
    // "아직 조회 안 함" 판정이 무너진다.
    params: restored.hasQuery ? restored.filter : EMPTY_FILTER,
  })
  /**
   * 엑셀 업로드로 교체한 행 — null 이면 조회 결과를 그린다. 업로드 뒤에는 화면에 서버 행이 하나도 없다.
   * [삭제] 로 업로드 행을 다 빼도 빈 배열로 남는다 — 조회 결과로 돌아가면 저장 안 한 업로드가 말없이 서버 행으로 바뀐다(봇 리뷰 #924).
   */
  const [importedRows, setImportedRows] = useState<GeneralOutboundRow[] | null>(null)
  /**
   * 편집 셀 입력분 — 키(_rowKey) → 표가 마지막으로 돌려준 행. 입력마다 다시 그리지 않는다(LOGISPRD-14196).
   * core.Table 은 키 하나마다 전체 행을 넘기는데, 그때마다 화면을 다시 그리면 화면·뷰·표가 다시 그려져 입력이 밀린다.
   * 입력값은 표가 자기 상태로 이미 보여 준다. 읽는 쪽은 `getEffectiveRows` 로 합쳐 읽고, 표가 다시 그려질 일이 생기기 전에 `syncEdits` 로 `rows` 에 얹는다.
   * 새로 받기 시작할 때(`discardLocal`)·업로드·초기화에서 비운다 — 나머지 구간이 들어와도 지우지 않아 받는 중 편집이 남는다.
   */
  const editsRef = useRef(new Map<string, Partial<GeneralOutboundRow>>())
  const mergeEdits = (base: GeneralOutboundRow[]): GeneralOutboundRow[] =>
    base.map((r) => {
      const edit = editsRef.current.get(r._rowKey)
      return edit ? withoutNewMarker({ ...r, ...edit }) : r
    })
  /** `syncEdits` 가 올린다 — 입력분을 `rows` 에 다시 얹으라는 신호다. */
  const [editsVersion, setEditsVersion] = useState(0)
  /** 입력분을 `rows` 에 얹는다 — 같은 값을 다시 얹을 뿐이라 여러 번 불러도 같다. ref 는 다음 교체 때 비운다. */
  const syncEdits = (): void => {
    if (editsRef.current.size) setEditsVersion((n) => n + 1)
  }
  const [importSeq, setImportSeq] = useState(0)
  const [skuFail, setSkuFail] = useState<{ title: string; message: string; barcodes: string[] } | null>(null)
  const [progressKey, setProgressKey] = useState<string | null>(null)
  /**
   * 조회영역에 지금 떠 있는 값(뷰가 조회·옵션 슬롯으로 넘겨 준다). 조회 조건(`queryParams.params`)과 다를 수 있다 —
   * 고르기만 하고 아직 조회하지 않은 상태다. 확정과 엑셀 업로드 게이트가 이 값을 본다.
   * 뷰는 표 슬롯(rows.ROWS)에 매 렌더 현재 조회영역 값을 넘기므로 거기서 받아 둔다.
   */
  const liveFilterRef = useRef<FilterValues>(restored.filter)
  /**
   * 조회를 실행한 적이 있는가. 복원된 화주가 있으면 참으로 시작해 마운트 시점에 조회가 돈다.
   *
   * 예전에는 조회 순번을 세어 queryKey 에 넣었는데, 마운트 지역 값이라 다른 메뉴를 다녀오면 0 부터
   * 다시 세어 캐시 키가 어긋났다. 조건이 같아도 전량 재조회했다(결정 12).
   */
  const [hasSearched, setHasSearched] = useState(restored.hasQuery)

  const { data: shipperOptions = [], error: shipperError, errorUpdatedAt: shipperErrorAt } = useGetShipperOptions()
  // 화주 목록 실패 — 전역 알럿(옛 디자인)을 끄고(훅 meta) 여기서 신규 디자인 알럿으로. 실패 시각이 바뀔 때만 다시 알린다.
  useEffect(() => {
    if (shipperError) notice.failure(MESSAGES.shipperLoadFailed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipperErrorAt])
  const shipperFilterOptions = useMemo(() => scopeSelectOptions(shipperOptions, scope), [shipperOptions, scope])
  const shipperName = useMemo(
    () => shipperOptions.find((o) => o.value === queryParams.params.SPR_NM)?.label ?? '',
    [shipperOptions, queryParams.params.SPR_NM],
  )

  // 진입 즉시 조회하지 않는다 — 화주코드가 필수라 조회 버튼으로만 실행한다. 전량을 받아 뷰가 10,000건씩 나눈다(LOGISPRD-15268).
  //  조회 실패 알럿은 공용 훅 meta(errorToast)로 전역이 띄운다 — 여기서 또 띄우면 두 번 뜬다.
  const list = useGetGeneralOutboundList(queryParams, hasSearched && Boolean(queryParams.params.SPR_NM))
  const { refetch, guardAction } = list
  const saveMutation = useSaveGeneralOutbounds()
  const confirmMutation = useConfirmGeneralOutbounds()
  const { data: progress, isExhausted: isPollExhausted } = useGetOutboundProgress(progressKey)

  const fetchedRows = useMemo(() => toRows(list.rows), [list.rows])
  const { onDownloadExcel } = useExcelDownload<GeneralOutboundDownloadParams>({ config: { method: 'post' } })
  /**
   * 표의 행 = 업로드 행 또는 받은 행, 그 위에 입력분. 받은 행을 로컬 state 로 옮기지 않는다 — 구간이 들어올 때마다 옮기면
   * 그 사이 입력이 지워지고, 같은 응답이면 react-query 가 옛 참조를 돌려줘(structuralSharing) 옮기는 시점도 못 잡는다.
   * 버리는 시점은 응답이 아니라 동작(조회·저장 뒤 재조회·업로드·초기화)이 정한다.
   */
  const rows = useMemo(
    () => {
      const base = importedRows ?? fetchedRows
      return editsRef.current.size ? mergeEdits(base) : base
    },
    // editsVersion = syncEdits 신호. mergeEdits 는 editsRef 만 읽는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [importedRows, fetchedRows, editsVersion],
  )
  const getEffectiveRows = (): GeneralOutboundRow[] => (editsRef.current.size ? mergeEdits(rows) : rows)
  /**
   * 새로 받기 시작 — 입력분·업로드 행을 버린다(그게 [조회]·저장 뒤 재조회의 뜻이다). 업로드 행을 남기면 저장 뒤에도 'I' 로 남아 같은 주문이 다시 INSERT 된다.
   * `only`([조회] 때 보이던 행)를 주면 그때 있던 입력분만 버린다 — 재조회를 기다리는 사이 새로 입력한 칸은 남긴다(keepEditedSince).
   * `editsVersion` 도 올린다 — 같은 응답이면 `fetchedRows` 참조가 그대로라 `rows` 메모가 다시 계산되지 않아 얹어 둔 입력이 남는다.
   */
  const discardLocal = useCallback((only?: Map<string, GeneralOutboundRow>): void => {
    if (only) keepEditedSince(editsRef.current, only)
    else editsRef.current.clear()
    setImportedRows(null)
    setEditsVersion((n) => n + 1)
  }, [])
  /** [조회] 차례 — 늦게 끝난 앞 조회가 뒤 조회의 입력분을 버리지 않게 비교한다. */
  const searchSeqRef = useRef(0)
  /** 저장·확정·삭제 첫 줄 — 받는 중·일부 실패면 guardAction 이 알리고, throw 로 뷰가 선택을 그대로 둔다(뷰 계약). */
  const guardOrThrow = (): void => {
    if (!guardAction()) throw markShown(new Error(FETCH_ALL_MESSAGES.fetching))
  }
  const reload = useCallback((): void => {
    discardLocal()
    refetch().catch(() => undefined)
  }, [discardLocal, refetch])

  // 저장은 워크플로에 접수만 된다 — 끝날 때까지 진행률 모달로 보여 주고, 사용자가 모달을 닫으면 목록을 다시 읽는다(1.0 GI31 진행률 모달).
  //  상태가 아직 RUNNING 이어도 진행률이 100 에 닿으면 끝난 것으로 본다(1.0 progress >= 100) — 모달은 COMPLETED 여야 [확인]을 풀어 준다.
  //  FAILED 는 진행률과 무관하게 실패다(1.0 은 FAILED 를 먼저 본다).
  const isProgressDone = Boolean(
    progress &&
      progress.status !== PROGRESS_FAILED &&
      (progress.status === PROGRESS_COMPLETED || (progress.total > 0 && progress.processed >= progress.total)),
  )
  const shownProgress = progress && isProgressDone ? { ...progress, status: PROGRESS_COMPLETED } : (progress ?? null)
  /** 진행률 모달이 맡은 저장이 [삭제] 였으면 그 건수 — 모달은 완료돼야 닫히므로 닫을 때 완료 토스트를 띄운다(BE 저장은 늘 워크플로라 슬롯이 토스트를 못 낸다). */
  const deleteCountRef = useRef(0)
  const handleProgressClose = useCallback((): void => {
    // FAILED 도 모달이 닫힐 수 있는 끝난 상태다 — 완료일 때만 알린다(기타출고와 같은 가드).
    const deleted = isProgressDone ? deleteCountRef.current : 0
    deleteCountRef.current = 0
    setProgressKey(null)
    reload()
    if (deleted) notice.success(DELETE_MESSAGES.done(deleted))
  }, [reload, isProgressDone])
  useEffect(() => {
    if (!progressKey) return

    /**
     * 폴링이 끝까지 못 가고 멈췄는데 화면이 모르면 `progressKey` 가 남아 저장·확정·업로드가 잠긴 채로
     * 끝난다. 상한 판정은 훅이 내보낸 걸 그대로 쓴다 — 화면이 따로 세면 훅과 정의가 어긋나고,
     * 실제로 화면은 성공만 세고 훅은 성공+실패를 세는 바람에 이 잠금이 안 풀리는 구멍이 있었다.
     */
    if (isPollExhausted) {
      setProgressKey(null)
      notice.failure(MESSAGES.progressUnavailable)
      return
    }

    // 완료는 모달이 맡는다(닫을 때 재조회). 실패는 1.0 progressPollingEpic 처럼 알럿을 띄우고 모달을 닫는다.
    if (!progress || isProgressDone) return
    if (progress.status === PROGRESS_FAILED) {
      setProgressKey(null)
      notice.failure(progress.message || '저장 중 오류가 발생했습니다.')
    }
  }, [progress, progressKey, isPollExhausted, isProgressDone])

  /**
   * 저장·확정은 되돌리기 어렵고 같은 행을 건드린다. 하나가 도는 동안 다른 하나가 열려 있으면
   * 처리 중인 행의 값이 바뀐 채로 컷오프가 나간다. 진행 상태를 한 곳에서 판단해 둘 다 잠근다.
   * 저장은 응답이 접수일 뿐이라 워크플로가 끝날 때까지(progressKey) 계속 진행 중으로 본다.
   */
  const isSaving = saveMutation.isPending || Boolean(progressKey)
  const isConfirming = confirmMutation.isPending
  // 나머지 구간을 받는 동안은 잠그지 않는다 — 표를 보고 고칠 수 있게 두고, 저장·확정·삭제·엑셀은 각 슬롯 첫 줄 guardAction 이 막는다.
  const isBusy = isSaving || isConfirming || list.isFirstLoading
  /**
   * 표에 실을 행 — key 를 _rowKey 로 준다(뷰 기본은 배열 인덱스라 재조회·페이지 이동 뒤 선택이 어긋난다).
   * 업로드 행에는 `__new` 를 단다 — core.Table 은 사용자가 만든 행만 처음부터 필수 칸을 검사해 빈 칸이 있으면 행을 빨갛게 칠한다(DS 계약).
   * 표시용 표식이라 상태(rows)에는 넣지 않는다(편집 콜백에서 다시 뗀다).
   */
  const viewRows = useMemo(
    () => rows.map((r) => ((r._edited === EDITED_FLAG.insert ? withNewMarker({ ...r, key: r._rowKey }) : { ...r, key: r._rowKey }))),
    [rows],
  )
  /**
   * 선택 행 → 현재 편집 상태의 행. 뷰가 넘기는 `ctx.selected` 는 선택 당시의 행 객체라 그 뒤 편집 셀로 바뀐 값이
   * 없을 수 있다 — 키로 지금 상태의 행을 다시 집는다(전량 × 선택수만큼 훑지 않게 Set 색인).
   */
  const pickSelected = (ctx: ActionCtx): GeneralOutboundRow[] => {
    const keys = new Set(ctx.selected.filter((r) => typeof r._rowKey === 'string').map((r) => r._rowKey as string))
    return getEffectiveRows().filter((r) => keys.has(r._rowKey))
  }

  /** 조회 조건을 메뉴 세션과 URL 에 남긴다. 셋을 함께 부르는 게 레포 규약이다(결정 12). */
  const persistSearch = useCallback(
    (filter: FilterValues, nextListQuery: string, page: number) => {
      const searchQuery = toSearchQuery({ filter, listQuery: nextListQuery, page })
      MenuStorage.menuOnSession.setSearch(searchQuery)
      menuSession.reload()
      replaceQuery(searchQuery)
    },
    [menuSession, replaceQuery],
  )

  /** 업로드 게이트 — 저장·확정 중이거나, 조회한 화주와 지금 고른 화주가 다르면 열지 않는다(업로드 행은 조회 화주로 저장된다). */
  const canUpload = useCallback((): boolean => {
    // 저장·확정이 도는 동안은 열지 않는다. 업로드는 목록을 통째로 교체하는데, 저장이 끝나면
    // 재조회가 서버 데이터로 다시 덮어써서 방금 올린 파일이 말없이 사라진다.
    // 진행 중 대기 — 실패 분류(알럿). 화면 규칙 기준 A.
    if (isBusy) {
      notice.failure(MESSAGES.uploadWhileBusy)
      return false
    }
    if (!guardAction()) return false
    // 업로드 행은 조회 화주로 저장된다. 필터에서 화주를 바꾸고 조회하지 않았다면 화면에 보이는 화주와
    // 실제 저장 화주가 달라진다. BE 도 요청 화주와 다른 행이 섞이면 저장을 통째로 거부한다.
    const selectedShipperCode = liveFilterRef.current.SPR_NM
    if (!queryParams.params.SPR_NM || (selectedShipperCode && selectedShipperCode !== queryParams.params.SPR_NM)) {
      notice.invalid(MESSAGES.uploadNeedsSearch)
      return false
    }
    return true
  }, [isBusy, guardAction, queryParams.params.SPR_NM])

  /**
   * 페이지 이동·목록 검색어 통지(뷰 onState). 뷰는 마운트 직후에도 한 번 부른다 — 복원값 그대로면 URL·세션을 다시 쓰지 않는다.
   */
  const lastViewRef = useRef({ page: restored.page, listQuery: restored.listQuery })
  /** 받는 중에 온 뷰 통지(페이지·목록 검색어) — 받기가 끝나면 처리한다(아래 flush effect, LOGISPRD-15349) */
  const pendingViewRef = useRef<ViewState | null>(null)

  /**
   * 저장 API 호출 — [저장]·[삭제] 가 같이 쓴다(삭제 = `_edited` 'D' 행). 성공 문구를 돌려주고(뷰가 토스트),
   * 워크플로로 넘어가면 `false`(진행률 모달이 결과를 맡는다). 실패는 알리고 throw(뷰가 선택을 유지).
   */
  const submitSave = async (targetRows: GeneralOutboundRow[]): Promise<string | false> => {
    const shipperCode = queryParams.params.SPR_NM
    // 지난 [삭제] 가 실패로 끝났으면 남아 있다 — 이번 저장 완료에 삭제 토스트가 붙지 않게 비운다.
    deleteCountRef.current = 0
    try {
      const res = await saveMutation.mutateAsync({
        MENU_CD: SAVE_MENU_CODE,
        ITEM_CD: SAVE_ITEM_CODE,
        USR_ID: userId,
        SPR_NM: shipperCode,
        DEPT_NM: user.DEPT_NM ?? '',
        shipper_code: shipperCode,
        shipper_name: shipperName || shipperCode,
        data: { headData: targetRows.map((r) => toSaveItem(r, shipperCode, shipperName)) },
      })
      if (!res.rtnChk) {
        throw markShown(failureError(res.message || '저장에 실패했습니다.'))
      }
      // 워크플로로 넘어간 저장은 진행률 모달이 결과를 맡는다(1.0 GI31·GI35 '작업 진행 중') — 토스트 없음(false).
      if (res.progressKey) {
        setProgressKey(res.progressKey)
        return false
      }
      reload()
      return res.message || '요청이 접수되었습니다.'
    } catch (e) {
      const skuFailure = asSkuFailure(e, '저장 실패')
      if (skuFailure) setSkuFail(skuFailure)
      else if (!isAlreadyShown(e)) notice.failure(apiMessage(e, '저장에 실패했습니다.'))
      throw markShown(e)
    }
  }

  /** 버리기 확인창이 떠 있는가 — 대기 중 재클릭을 막는다. */
  const discardConfirmOpenRef = useRef(false)
  /**
   * 저장하지 않은 편집·업로드 행이 있으면 버려도 되는지 묻는다 — [초기화]·메뉴 이동이 같이 쓴다(LOGISPRD-14801). 없으면 묻지 않는다.
   * 편집분은 `syncEdits` 뒤에도 `editsRef` 에 남아 있다(다음 교체 때 비운다) — 그래서 ref 크기와 업로드 행('I')만 보면 된다.
   * 확인창 대기 중 재클릭은 무시한다 — 확인창이 겹치고 두 답이 모두 진행을 돌리지 않게(기타출고 페이지 이동 가드와 같다).
   */
  const confirmDiscard = (message: string, okText: string): boolean | Promise<boolean> => {
    if (!editsRef.current.size && !rows.some((r) => r._edited)) return true
    if (discardConfirmOpenRef.current) return false
    discardConfirmOpenRef.current = true
    return new Promise<boolean>((resolve) => {
      const answer = (ok: boolean) => {
        discardConfirmOpenRef.current = false
        resolve(ok)
      }
      PboConfirm.show(message, { onConfirm: () => answer(true), onClose: () => answer(false), okText })
    })
  }
  // 상단 탭·왼쪽 메뉴로 떠나면 화면이 언마운트되어 행이 사라진다 — 떠나기 전에 묻는다.
  useLeaveGuard(() => confirmDiscard(MESSAGES.leaveUnsaved, '이동'))

  // ── 뷰 배선(Wiring) — 옛 핸들러의 본문을 슬롯에 잇는다. 필터 폼·표 선택·목록 검색·페이저·모달 열림은 뷰가 가진다. ──
  //  배선 객체는 매 렌더 새로 만든다(뷰가 렌더 첫머리에 props.wiring 을 모듈 홀더에 대입한다 — 문서 51 ⑪).
  /** 페이지 이동·목록 검색어 확정을 받아 둔다(onState 본문 — 받는 중 미룬 통지도 여기로 온다). */
  const applyViewState = (s: ViewState) => {
    const next = s.pager.pager
    const nextListQuery = s.listq['table.q']
    if (lastViewRef.current.page === next.page && lastViewRef.current.listQuery === nextListQuery) return
    // 페이지 이동 판정은 **뷰가 마지막으로 알린 페이지** 기준이다 — 조회에 쓰인 페이지(queryParams.page)와 비교하면, 화주 없는 [조회] 가
    // 뷰 페이저만 1로 되돌린 뒤(위 search 주석) 목록 검색을 "페이지 이동" 으로 오인해 옛 조건 1페이지를 재조회하고 편집 행을 덮는다(리뷰 gyoungjun 09:02).
    // 페이지를 옮기면 뷰가 검색어를 '' 로 함께 보낸다 — 검색어가 남은 채 페이지만 바뀐 것은 행이 줄어 뷰가 마지막 페이지로 맞춘 것이라 아래 검색어 경로로 간다(LOGISPRD-15349).
    if (next.page !== lastViewRef.current.page && nextListQuery === '') {
      // 전량을 받아 뒀으므로 다시 조회하지 않는다. 뷰는 페이지를 넘기며 검색칸을 비운다.
      lastViewRef.current = { page: next.page, listQuery: '' }
      // 뷰가 표를 다시 마운트했다 — 입력값이 다음에 돌아와도 보이게 넣는다.
      syncEdits()
      // 보던 페이지도 남긴다 — 조건만 남고 1페이지로 돌아가면 사람은 새로고침됐다고 느낀다.
      // 저장할 조건은 조회영역 값이 아니라 **실제로 조회에 쓰인** 조건이다. 폼만 바꾸고 조회하지
      // 않은 값을 저장하면, 복귀했을 때 떠나기 직전 화면과 다른 결과가 뜬다.
      persistSearch(queryParams.params, '', next.page)
      return
    }
    lastViewRef.current = { page: next.page, listQuery: nextListQuery }
    // 검색어가 바뀌면 표에 들어가는 행이 바뀌어 표가 자기 상태를 `rows` 로 되돌린다 — 입력분을 먼저 넣어 둔다.
    syncEdits()
    // 조회에 쓰인 조건으로 저장한다 — 위 페이지 이동과 같은 이유다.
    persistSearch(queryParams.params, nextListQuery, next.page)
  }
  // 받기가 끝나면 받는 중 미뤄 둔 통지를 처리한다 — 끝난 렌더에서 뷰가 다시 알렸으면 onState 가 먼저 비워 두어 여기선 할 일이 없다(자식 effect 가 먼저 돈다).
  useEffect(() => {
    if (list.isFirstLoading || list.isFetchingRest || pendingViewRef.current == null) return
    const pending = pendingViewRef.current
    pendingViewRef.current = null
    applyViewState(pending)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 받기가 끝나는 순간에만 돈다(applyViewState 는 매 렌더 새로 만든다)
  }, [list.isFirstLoading, list.isFetchingRest])

  // 화면관리 그리드 설정(LOGISPRD-17254) — 표 별표(생성본 permCols)와 [저장] 필수 검사가 같은 forTable(TABLE_ID).columns 를 지난다.
  //  BE 가 비면 거부하는 칸(BE_REQUIRED_KEYS)은 설정이 필수를 꺼도 별표·검사에 남긴다.
  const permissionSlot = usePermissionSlot('gi37', GRID_CDS)
  const permission = useMemo(() => withRequired(permissionSlot, { [TABLE_ID]: BE_REQUIRED_KEYS }), [permissionSlot])
  const requiredColumns = useMemo(() => toRequiredColumns(permission.forTable(TABLE_ID).columns(COLUMNS as core.CoreColumnType[])), [permission])

  const wiring: Wiring = {
    permission,
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { [TABLE_ID]: coreTableLayoutSlot(GRID_CDS[TABLE_ID]) },
    rows: {
      // 전량 + 클라이언트 페이징 — total 을 주지 않아 '전체 N'·페이지 수가 받은 만큼 늘어난다(받는 중 진행 표시, LOGISPRD-15267 규약).
      //  받는 동안(첫 구간·나머지 구간·재조회)은 받은 행 위에 로더를 띄워 표를 막는다(LOGISPRD-15390).
      ROWS: (_demo, q) => {
        // 조회영역 현재 값 — 뷰가 매 렌더 넘겨 준다. 확정·업로드 게이트가 "지금 떠 있는 값" 을 본다(1.0 FilterBar.js onSend 파리티).
        if (q.filters) liveFilterRef.current = lockFilter(toFilter(q.filters))
        return { rows: viewRows as unknown as Row[], loading: list.isFirstLoading || list.isFetchingRest }
      },
    },
    actions: {
      /** [조회] — 조회영역 값(키 = filterKey)을 옛 필터 컴포넌트와 같은 변환으로 BE 조건으로 만든다. */
      search: (filters) => {
        const filter = lockFilter(toFilter(filters))
        liveFilterRef.current = filter
        if (!filter.SPR_NM) {
          // throw = "조회 안 함" — 뷰(생성본 [조회] onClick, pbo-design #591)는 슬롯이 throw·false 면 페이저·선택·검색칸을 되돌리지 않는다.
          // 옛 화면(알럿만 띄우고 끝)과 같다. 여기서 페이저·검색어 상태를 건드리면 뷰와 어긋난다 — 예전 생성본(슬롯 전에 되돌림)을 위해
          // lastViewRef·listQuery 를 미리 비우던 방어는 #591 생성본에서 오히려 '전체 N' 을 서버 총건수로 되돌려 검색 중인 표와 안 맞았다(봇 리뷰 #798).
          throw markShown(invalidError(MESSAGES.shipperRequired))
        }

        const next: GeneralOutboundListParams = { MENU_CD: SEARCH_MENU_CODE, USR_ID: userId, params: filter }
        const nextKey = generalOutboundQueryKeys.list(next)
        // 조건이 같으면 지금 행이 그대로 남는다 — 입력분·업로드 행은 다시 받기에 성공한 뒤 버린다(실패하면 남아 입력이 보인다).
        //  조건이 다르면 표가 새 조건의 행으로 바뀌므로 지금 버린다(옛 조건 행의 입력이 새 행에 얹히지 않게).
        const sameKey = hashKey(nextKey) === hashKey(generalOutboundQueryKeys.list(queryParams))
        if (sameKey) syncEdits()
        else discardLocal()
        const seq = ++searchSeqRef.current
        // 업로드 행은 재조회 성공 때 버려진다 — 그 행의 입력분도 남기지 않게 기준에서 뺀다.
        const shownAtSearch = new Map(importedRows ? [] : getEffectiveRows().map((row) => [row._rowKey, row]))
        setQueryParams(next)
        setHasSearched(true)
        lastViewRef.current = { page: 1, listQuery: '' }
        pendingViewRef.current = null
        persistSearch(filter, '', 1)

        // 같은 조건으로 다시 눌러도 서버에 묻는다(1.0 동작). 키가 그대로면 자동 조회가 안 돌기 때문이다.
        // `refetch()` 를 쓰지 않는 이유는 최초 진입처럼 쿼리가 비활성인 구간에서는 아무 일도 안 하기 때문이다(결정 12).
        // 조건이 바뀐 경우는 키가 달라져 스스로 돌고, 이미 도는 중이면 react-query 가 합쳐 한 번만 나간다.
        queryClient
          .invalidateQueries({ queryKey: nextKey })
          .then(() => {
            // 그 사이 다시 조회했으면 입력분은 뒤 조회가 정한다.
            if (seq === searchSeqRef.current && sameKey && queryClient.getQueryState(nextKey)?.status === 'success') discardLocal(shownAtSearch)
          })
          .catch(() => undefined)
      },
      /**
       * 편집 셀 — 표는 보이는 행(목록 검색으로 걸러진 뒤)만 돌려준다. 그대로 쓰면 가려진 행이 사라지므로 키로 합친다.
       * 키 입력마다 불린다 — `rows` 에 넣지 않고 ref 에만 둔다(editsRef 주석).
       */
      ROWS_rowsChange: (changed) => {
        changed.forEach((r) => {
          if (typeof r._rowKey === 'string') editsRef.current.set(r._rowKey, r as Partial<GeneralOutboundRow>)
        })
      },
      /**
       * [저장] — 1.0 GI31.js onHandleSave 와 같은 화면 검증을 서버 왕복 전에 잡는다. 성공 문구는 돌려주어 뷰가 토스트한다.
       * 뷰 계약(컨덕터 #582): 실패는 **throw**(뷰가 선택 유지·모달 유지) · false = 성공인데 토스트 없음 · 문자열 = 그 문구로 토스트.
       * 알럿은 여기서 띄우고 throw 한다(뷰의 거부 핸들러는 아무것도 하지 않는다).
       */
      genOutSaveButton: async (ctx): Promise<string | false> => {
        guardOrThrow()
        // 저장이 끝나면 뷰가 표를 다시 마운트한다(선택 해제) — 그때 입력분이 `rows` 에 없으면 옛 값으로 돌아간다.
        syncEdits()
        const selectedRows = pickSelected(ctx)
        const shipperCode = queryParams.params.SPR_NM
        const invalid = validateBeforeSave(selectedRows, shipperCode, requiredColumns)
        if (invalid) {
          // 필수값 누락은 "N번째 행의 …" 로 행을 짚는다(rows.findMissingField) — 놓치면 못 찾으니 알럿. 그 밖의 공통 문구(MESSAGES)는 토스트.
          throw isRowMessage(invalid) ? rowInvalidError(invalid) : invalidError(invalid)
        }

        return submitSave(selectedRows)
      },
      /**
       * [삭제] — 선택 행을 확인창을 거쳐 바로 지운다(일반입고 2.0 [삭제] 와 같은 방식, LOGISPRD-14426).
       * 전용 API 가 없어 저장 API 에 EDITTED 'D' 행으로 보낸다(1.0 GI31 ToolBar 삭제 → 저장과 같은 계약). BE 는 행 `ID` 로만 지우고
       * 필수값 검증·SKU 판정에서 뺀다 — 출고확정된 주문이 섞이면 전량 거절한다. 값은 편집 전 행(`rows`)을 싣는다.
       * 미저장 업로드 행('I')은 ID 가 없어 표에서만 뺀다. 확인창을 닫으면 throw — 뷰가 선택을 그대로 둔다.
       */
      genOutDeleteButton: async (ctx): Promise<string | false> => {
        guardOrThrow()
        syncEdits()
        const keys = new Set(pickSelected(ctx).map((r) => r._rowKey))
        if (!keys.size) throw invalidError(MESSAGES.selectRequired)
        const saved = rows.filter((r) => keys.has(r._rowKey) && r._edited !== EDITED_FLAG.insert)
        if (saved.length > MAX_SAVE_SIZE) throw invalidError(MESSAGES.deleteSizeExceeded)

        await new Promise<void>((resolve, reject) => {
          PboConfirm.show(DELETE_MESSAGES.confirm(keys.size), {
            onConfirm: resolve,
            onClose: () => {
              const cancelled = new Error(MESSAGES.deleteCancelled)
              markShown(cancelled)
              reject(cancelled)
            },
            okText: '삭제',
          })
        })

        if (saved.length) {
          const result = await submitSave(saved.map((r) => ({ ...r, _edited: EDITED_FLAG.delete })))
          if (result !== false) return DELETE_MESSAGES.done(saved.length)
          // 워크플로로 넘어갔다 — 진행률 모달을 닫을 때 알린다(handleProgressClose).
          deleteCountRef.current = saved.length
          return false
        }
        keys.forEach((key) => editsRef.current.delete(key))
        // 여기 오는 행은 모두 업로드 행('I')이다 — 받은 행에는 ID 가 있다.
        setImportedRows((prev) => prev && prev.filter((r) => !keys.has(r._rowKey)))
        return DELETE_MESSAGES.done(keys.size)
      },
      /**
       * [확정] — 마지막 조회 조건이 아니라 조회영역에 지금 떠 있는 값을 쓴다(1.0 FilterBar.js onSend).
       * 성공 안내는 데모 정의(토스트) + 다른 컴포넌트 모드 화면(gl01 확정·수집·저장)과 같은 규칙 — BE 문구(차수 번호가 담긴다)를 돌려줘
       * 뷰가 토스트로 띄우고 선택을 푼다. 옛 화면의 알럿(MAlert)은 1.0 방식이라 쓰지 않는다(PD 확정 2026-09-21).
       */
      genOutConfirmButton: async (ctx): Promise<string> => {
        guardOrThrow()
        // 조회 뒤에 주문유형을 고르고 [확정] 을 누르는 흐름이 실제 사용법인데, 조회 스냅샷을 보면 그 값이 없어
        // "주문유형을 선택해주시기 바랍니다" 가 뜬다.
        const filter = liveFilterRef.current
        syncEdits()
        const selectedRows = pickSelected(ctx)
        const invalid = validateBeforeConfirm(selectedRows, filter)
        if (invalid) throw invalidError(invalid)

        const targets: ConfirmGeneralOutboundItem[] = selectedRows.map((r) => ({
          ID: r.ID,
          SPR_NM: r.SPR_NM,
          ORD_NO: r.ORD_NO,
          ORD_OPT_NO: r.ORD_OPT_NO,
          ORD_TYPE: r.ORD_TYPE,
          TRS_TYPE: r.TRS_TYPE,
        }))

        try {
          const res = await confirmMutation.mutateAsync({
            params: { ...filter, USR_ID: userId, MENU_CD: SAVE_MENU_CODE },
            data: targets,
          })
          if (!res.result.result) {
            throw markShown(failureError(res.message || '확정에 실패했습니다.'))
          }
          reload()
          // 성공 문구에는 차수 번호가 담긴다. 비어 오면 최소한 접수됐다는 사실은 알린다(gl01 과 같은 폴백 규칙).
          return res.message || '확정 요청이 전송되었습니다.'
        } catch (e) {
          const skuFailure = asSkuFailure(e, '확정 실패')
          if (skuFailure) setSkuFail(skuFailure)
          else if (!isAlreadyShown(e)) notice.failure(apiMessage(e, '확정에 실패했습니다.'))
          throw markShown(e)
        }
      },
      /**
       * 엑셀 다운로드 모달 [확인] — BE 가 마지막 조회 조건(`queryParams`)으로 다시 뽑아 비밀번호 zip 으로 내려준다(LOGISPRD-15797·15805).
       * 생성본 모달에는 [확인] 잠금이 없어 사유코드·상세사유(5~200자)·비밀번호를 여기서 막는다(throw = 모달 유지). 비밀번호 규칙은 BE `PasswordValidator` 미러(utils/downloadPassword).
       * 감사로그(사유)는 BE `@AccessAudit` 가 남긴다 — FE 감사 API 를 또 부르면 두 번 남는다.
       * 실패 안내는 useExcelDownload 가 띄우고 resolve 한다 — 성공·실패를 못 가르므로 성공 토스트 없이 모달을 닫는다(RGR05 와 같음).
       */
      genOutDownloadModal_ok: async (ctx): Promise<undefined> => {
        // 모달이 열린 뒤 재조회가 시작됐을 수 있다 — 받는 중인 조건으로 파일을 뽑지 않는다.
        guardOrThrow()
        const reason = text(ctx.form?.reason)
        const detail = text(ctx.form?.detail).trim()
        const password = text(ctx.form?.password)
        if (!reason || detail.length < REASON_DETAIL_MIN_LENGTH || detail.length > REASON_DETAIL_MAX_LENGTH) {
          throw invalidError(MESSAGES.downloadReasonInvalid)
        }
        if (!isValidDownloadPassword(password)) throw invalidError(DOWNLOAD_PASSWORD_INVALID_MESSAGE)
        // 조회와 같은 본문을 문자열로 싣는다 — BE 가 조회 파싱을 그대로 쓴다. page·pageSize 는 BE 가 무시하고 상한까지 뽑는다.
        const params = JSON.stringify({ ...queryParams, page: 1, pageSize: GENERAL_OUTBOUND_FETCH_SIZE })
        await onDownloadExcel(GENERAL_OUTBOUND_DOWNLOAD_URL, { params, reasonCode: reason, detailedReason: detail, password })
        return undefined
      },
      /**
       * 엑셀 업로드 모달 [업로드] — 서버 업로드 API 는 1.0 에도 없다. 파일을 열어 표를 신규 행으로 **교체**하고, 사용자가 검토한 뒤 [저장]으로 보낸다(1.0 TOOL_IMP 파리티).
       * ⚠ 1.0 은 교체 직후 `selectAll()` 로 전량을 골라 뒀는데, 표 선택은 뷰 소유라 여기서 고를 수 없다(컨덕터 후속: 업로드 뒤 선택 슬롯).
       *   지금은 올린 사람이 [저장] 전에 전체 선택을 한 번 눌러야 한다.
       */
      genOutUploadModal_ok: async (ctx): Promise<string> => {
        const file = ctx.form?.file
        if (!(file instanceof File)) {
          throw invalidError(MESSAGES.uploadFileRequired)
        }
        // 파서(ExcelJS xlsx.load)는 .xlsx 만 읽는다 — 다른 파일은 JSZip 영어 오류가 알럿에 그대로 뜬다(봇 리뷰). 드래거 accept 는 힌트일 뿐이라 여기서 막는다.
        if (!file.name.toLowerCase().endsWith('.xlsx')) {
          throw invalidError(MESSAGES.uploadXlsxOnly)
        }
        // 모달을 열어 둔 사이에 저장이 시작됐거나 화주를 바꿨을 수 있다. 그대로 반영하면 저장이 끝나며 재조회가 덮어쓴다.
        if (!canUpload()) throw markShown(new Error(MESSAGES.uploadNeedsSearch))
        try {
          const imported = await parseGeneralOutboundExcel(file)
          if (!imported.length) {
            throw markShown(invalidError('가져올 데이터 행이 없습니다.'))
          }
          const shipperCode = queryParams.params.SPR_NM
          // 누적이 아니라 교체다. 1.0 공용 툴바가 `setRowData(result)` 로 목록을 통째로 갈아 끼웠다
          // (moms-fe-1.0 ToolBar.js:296-297). 누적하면 같은 파일을 두 번 올릴 때 같은 주문이 두 벌
          // 쌓이고 둘 다 저장에 실린다 — 되돌리려면 DB 에서 지워야 한다.
          const nextRows = imported.map((item, i) => toNewRow(item, shipperCode, importSeq + i))
          editsRef.current.clear()
          setImportedRows(nextRows)
          callToAddImportLog({ menuCode: EXPORT_LOG_MENU_CD, apiName: `${EXPORT_LOG_MENU_CD}_LIST_IMPORT`, rowCount: nextRows.length })
          setImportSeq((n) => n + imported.length)
          // 한 페이지(10,000)를 넘으면 저장·확정이 현재 페이지 선택분만 나간다고 알린다.
          noticeIfUploadExceedsPage(nextRows.length)
          // 목록 검색이 걸려 있으면 방금 넣은 행이 걸러져 보이지 않는다 — 뷰는 업로드 성공 뒤 검색칸·표 필터를 스스로 비운다(생성본 onOk, pbo-design #575).
          return `${imported.length}행으로 목록을 교체했습니다. 검토 후 저장하세요.`
        } catch (e) {
          if (!isAlreadyShown(e)) notice.failure(e instanceof Error ? e.message : '엑셀 파일을 읽을 수 없습니다.')
          throw markShown(e)
        }
      },
    },
    options: {
      /** 화주명 — 세션 화주 목록(ST11 선례). 데모의 화주명 목데이터(무신사스탠다드 등)는 코드가 아니라 조회에 쓸 수 없다. */
      GENOUTSHIPPER_OPTIONS: () => shipperFilterOptions,
      // 주문유형·배송유형은 BE 열거형이다 — 데모 목록의 폐기·테스트·증정·재고이동은 2.0 에 없어 고르면 반드시 400 이 난다(consts 주석).
      GENOUTORDTYPE_OPTIONS: () => ORDER_TYPE_OPTIONS,
      GENOUTTRSTYPE_OPTIONS: () => TRANSPORT_TYPE_OPTIONS,
      // 편집 셀은 저장이 허용하는 값만 — 그 밖의 값은 BE validateSave 가 반드시 거부한다.
      ROWS_ORD_TYPE: () => SAVABLE_ORDER_TYPE_OPTIONS,
      ROWS_TRS_TYPE: () => SAVABLE_TRANSPORT_TYPE_OPTIONS,
      // 사유코드 — 2.0 콤보에 DOWNLOAD_CD 가 없어 1.0 MOMS_COMBO_INFO 실측값 상수(consts 주석). 콤보 API 가 생기면 훅으로 바꾼다.
      REASONRADIO_OPTIONS: () => DOWNLOAD_REASON_OPTIONS,
    },
    // 조회 기본값(화주 = 기본 화주 코드 — 데모의 "무신사스탠다드" 는 라벨) · 진입 초기값(URL 복원 조건). [초기화] 는 기본값으로 돌아간다.
    //  이 슬롯은 마운트 때 한 번만 동기로 읽혀 늦게 오는 옵션(화주 목록)을 못 본다 — 세션 화주가 없는 계정은 아래 filterFallback 이 채운다.
    /** 화주 고정 계정은 화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 고를 화주가 둘 이상이면 연다(LOGISPRD-15609). 보이는 것만 잠그므로 값은 기본값·search 가 따로 고정한다 */
    filterDisabled: canSelectShipper(scope) ? undefined : { SPR_NM: true },
    filterDefaults: { genOutFilterCard: (demo) => ({ ...demo, SPR_NM: defaultShipperCode }) },
    filterInitial: { genOutFilterCard: (defaults) => ({ ...defaults, ...restoredFilterValues(restored.filter) }) },
    /**
     * 늦게 오는 기본값 — 화주 목록이 도착한 뒤 화주가 비어 있으면 첫 화주로(옛 화면 `shipperOptions[0]` 폴백, 리뷰 daehwanryu 2026-09-21 · pbo-design #581).
     * 뷰는 옵션이 바뀔 때마다·[초기화] 뒤에 부르고 **비어 있는 키만** 채운다 — 사용자가 고른 화주는 덮지 않는다.
     * 옛 화면은 "세션 화주가 목록에 없어도" 첫 화주로 바꿨는데, 그건 세션값을 말없이 다른 화주로 바꾸는 것이라 여기서는 비어 있을 때만 채운다(직접 고르게).
     */
    filterFallback: {
      genOutFilterCard: (current, { GENOUTSHIPPER_OPTIONS }) => {
        const first = GENOUTSHIPPER_OPTIONS?.[0]?.value
        return current.SPR_NM || first === undefined ? undefined : { SPR_NM: first }
      },
    },
    useBusy: () => isBusy,
    opens: {
      genOutUploadButton: () => canUpload(),
      // 조회해 받은 행으로 판정한다 — 목록 검색어로 화면이 0건이어도 BE 는 조회 조건 전체를 내려준다.
      genOutExcelButton: () => {
        if (!guardAction()) return false
        if (!fetchedRows.length) {
          notice.invalid(MESSAGES.excelNoRows)
          return false
        }
        return true
      },
    },
    // 페이저 크기는 뷰 기본(10,000 — 1.0 공통 페이지 크기, LOGISPRD-15268 에서 GI31 의 5,000 을 통일).
    stateInitial: { pager: { pager: { page: restored.page } }, listq: { 'table.q': restored.listQuery } },
    /** 페이지 이동·목록 검색어 확정 — 옛 handlePage / onSearchList. */
    onState: (s) => {
      // 받는 중에는 저장을 미룬다 — 덜 온 행으로 뷰가 마지막 페이지로 줄여 알린 값이 복원 페이지를 덮는다(LOGISPRD-15349).
      //  받는 중 사용자가 옮긴 페이지·검색어도 여기로 오므로 버리지 않고 마지막 통지를 두었다가 받기가 끝나면 처리한다.
      if (list.isFirstLoading || list.isFetchingRest) {
        // 표는 지금 다시 그려진다 — 입력분은 바로 넣어 둔다(저장만 미룬다).
        syncEdits()
        pendingViewRef.current = s
        return
      }
      pendingViewRef.current = null
      applyViewState(s)
    },
    /**
     * [초기화] — 조회를 트리거하지 않는다. 목록도 함께 비워 다음 조회 전 상태를 분명히 한다.
     * 화주가 빈 조건이 되면서 조회가 자동으로 멎는다(enabled). 조회영역 값·페이저·선택·검색어는 뷰가 되돌린다.
     */
    /** [초기화] 전 — 저장하지 않은 행이 있으면 물어본다. 취소하면 조회영역 값·행을 그대로 둔다. */
    canReset: () => confirmDiscard(MESSAGES.resetUnsaved, '초기화'),
    onReset: () => {
      setQueryParams((prev) => ({ ...prev, params: EMPTY_FILTER }))
      discardLocal()
      setHasSearched(false)
      liveFilterRef.current = { SPR_NM: defaultShipperCode }
      lastViewRef.current = { page: 1, listQuery: '' }
      pendingViewRef.current = null
      /**
       * 저장된 조건도 함께 비운다 — 다음 진입이 기본값으로 열리게 한다.
       *
       * `reload()` 가 빠지면 sessionStorage 만 비고 탭 스트립이 쓰는 React state 는 옛 검색어를 그대로
       * 들고 있다. 탭을 다녀오면 그 state 로 `navigate(link + search)` 해서 **지운 조건이 살아 돌아온다.**
       * 조회 경로와 같은 3연타여야 한다.
       */
      MenuStorage.menuOnSession.setSearch({})
      menuSession.reload()
      replaceQuery({})
    },
  }

  return (
    // 한국어 로케일 — 생성본 ConfigProvider 는 테마만 줘 날짜 칸 등 antd 문구가 영어였다(PboKoLocale 주석).
    <PboKoLocale>
      <GeneralOutboundPageView wiring={wiring} />
      {/* dev 전용 오류 모달 — 뷰의 ConfigProvider 밖이라 같은 pbo 테마를 다시 건다(봇 리뷰: 사유 모달과 외형이 달라짐). */}
      <core.ConfigProvider theme={pboLogisticsTheme}>
        <SkuFailModal
          open={Boolean(skuFail)}
          title={skuFail?.title ?? '저장 실패'}
          message={skuFail?.message ?? ''}
          barcodes={skuFail?.barcodes ?? []}
          onClose={() => setSkuFail(null)}
        />
        <OutboundProgressModal open={progressKey !== null} progress={shownProgress} onClose={handleProgressClose} />
      </core.ConfigProvider>
    </PboKoLocale>
  )
}

/**
 * 재고통합 FF 첫 조회를 기다린 뒤 화면을 연다 — 화주 범위·URL 복원값은 마운트 첫 렌더에 한 번 만들어지므로
 * FF 값이 정해지기 전에 만들면 잠긴 29CM·INOOINT 의 MUSINSA 복원이 자기 화주로 덮인다(LOGISPRD-15609).
 */
function GeneralOutbound() {
  const { ready, coMingle } = useCoMingleShipperScope()
  if (!ready) return <Loader />
  return <GeneralOutboundScreen coMingle={coMingle} />
}

export default GeneralOutbound
